"use client";

import { useEffect, useState } from "react";
import { ArrowRight } from "lucide-react";
import Link from "next/link";
import WhaleLogo from "@lattice/frontend/components/brand/whale-logo";
import { Button } from "@lattice/frontend/components/ui/button";
import { cn } from "@lattice/frontend/lib/utils/ui";
import { useLandingCta } from "@lattice/frontend/components/landing/redesign/use-landing-cta";
import { E_Page, routes } from "@lattice/frontend/lib/routes/routes";

export default function LandingNavbarClient() {
  const [isScrolled, setIsScrolled] = useState(false);
  const { isAuthed } = useLandingCta();
  // Web sign-up is closed during build mode, so the logged-out CTA points at
  // the waitlist on the root page instead of into an account-creation flow.
  // The separate "Log in" link above still serves returning users.
  const ctaHref = isAuthed ? E_Page.Shell : "/";
  const ctaLabel = isAuthed ? "Return to Prism" : "Join the waitlist";

  // eslint-disable-next-line no-restricted-syntax -- add background styling to navbar on scroll past threshold
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div>
      <nav
        className={cn(
          "h-14 lg:h-16 fixed left-0 right-0 z-50 border-b border-white/10 bg-black/80 transition-all duration-300",
          {
            "backdrop-blur-lg bg-black/60": isScrolled,
          },
        )}
      >
        <div className="h-full max-w-[74rem] mx-auto px-4 md:px-6 flex items-center justify-between">
          <div className="flex items-center">
            <Link
              prefetch={false}
              href="/"
              className="flex items-center gap-2 text-white"
            >
              <WhaleLogo className="h-8 w-auto" />
              <span className="text-xl font-semibold tracking-tight">
                Lattice
              </span>
            </Link>
          </div>
          <div className="flex items-center gap-4 md:gap-5">
            {!isAuthed ? (
              <Link
                prefetch={false}
                href={routes.authSignIn()}
                className="text-base text-slate-300 hover:text-white transition-colors"
              >
                Log in
              </Link>
            ) : null}

            {/* Desktop CTA */}
            <Button
              asChild
              className="hidden lg:inline-flex h-11 px-5 text-base bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white border-0 hover:from-violet-500 hover:to-fuchsia-500 shadow-[0_10px_30px_-12px_rgba(124,58,237,0.6)]"
            >
              <Link prefetch={false} href={ctaHref}>
                {ctaLabel}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>

            {/* Mobile CTA */}
            <Button
              asChild
              className="lg:hidden h-9 px-4 text-sm font-semibold bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white border-0 hover:from-violet-500 hover:to-fuchsia-500"
            >
              <Link prefetch={false} href={ctaHref}>
                {ctaLabel}
              </Link>
            </Button>
          </div>
        </div>
      </nav>
      <div className="h-14 lg:h-16" />
    </div>
  );
}
